import { SpeciesMark } from "@/app/components/PlantMark";
import { BackIcon, XIcon } from "@/app/components/system/Icons";
import { Markdown } from "@/app/components/Markdown";
import type { Species } from "@/types";
import { useState, type ReactNode } from "react";

export function SpeciesCard({ species, kicker }: Readonly<{
  species: Omit<Species, "id"> & { id?: string } | null;
  kicker?: ReactNode;
}>) {
  const [labelOpen, setLabelOpen] = useState(false);
  if (!species) return null;
  const labelImageUrl = species.labelImageUrl;
  const recommendations = species.recommendations?.trim();

  return (
    <>
      <section className="mt-2 mb-5.5 bg-paper border border-line rounded-2xl overflow-hidden">
        <div className="flex items-center gap-3 px-3.5 py-3">
          <SpeciesMark
            species={species}
            size={56}
          />
          <div className="min-w-0 flex-1">
            {kicker}
            <h3 className="font-display text-[17px] m-0 leading-tight tracking-tight text-ink truncate">
              {species.common || species.scientific}
            </h3>
            {species.common && species.scientific && (
              <p className="serif-it text-[13px] m-0 mt-0.5 text-ink-3 truncate">{species.scientific}</p>
            )}
          </div>
        </div>

        {recommendations ? (
          <div className="border-t border-line/60 px-3.5 py-3">
            <Markdown>{recommendations}</Markdown>
          </div>
        ) : (
          <div className="border-t border-line/60 px-3.5 py-3">
            <p className="text-[13px] m-0 text-ink-3 leading-[1.4]">No care recommendations yet.</p>
          </div>
        )}

        {labelImageUrl && (
          <button
            type="button"
            onClick={() => setLabelOpen(true)}
            className="border-t border-line/60 w-full flex items-center gap-3 px-3.5 py-3 text-left cursor-pointer transition-colors duration-150 hover:bg-cream-2"
          >
            <img
              src={labelImageUrl}
              alt=""
              className="shrink-0 w-14 h-14 rounded-lg object-cover bg-cream-2 border border-line"
            />
            <div className="min-w-0 flex-1">
              <p className="text-[10.5px] tracking-widest uppercase text-ink-3 m-0 mb-0.5 font-medium">Original label</p>
              <p className="text-[13px] m-0 text-ink leading-[1.35]">Tap to view full photo</p>
            </div>
            <span className="rotate-180 inline-flex text-ink-3"><BackIcon size={14} stroke={2} /></span>
          </button>
        )}
      </section>

      {labelOpen && labelImageUrl && (
        <div
          onClick={() => setLabelOpen(false)}
          className="fixed inset-0 z-250 flex items-center justify-center p-7 cursor-zoom-out overflow-auto"
          style={{ background: "oklch(0 0 0 / 0.78)", animation: "scan-overlay-fade 0.18s ease-out" }}
        >
          <button
            type="button"
            onClick={() => setLabelOpen(false)}
            aria-label="Close"
            className="absolute top-4 right-4 w-11 h-11 rounded-full text-ink grid place-items-center cursor-pointer hover:bg-paper"
            style={{
              background: "oklch(1 0 0 / 0.92)",
              border: "1px solid oklch(1 0 0 / 0.5)",
              boxShadow: "0 6px 18px -8px oklch(0 0 0 / 0.5)",
            }}
          >
            <XIcon size={20} stroke={2} />
          </button>
          <img
            src={labelImageUrl}
            alt="Scanned plant care label"
            onClick={(e) => e.stopPropagation()}
            className="w-auto h-auto rounded-2xl cursor-default block"
            style={{
              maxWidth: "calc(100vw - 56px)",
              maxHeight: "calc(100vh - 56px)",
              objectFit: "contain",
              boxShadow: "0 30px 60px -10px oklch(0 0 0 / 0.6)",
            }}
          />
        </div>
      )}
    </>
  );
}
